import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ExternalOrderItem } from '@1po/1po-bff-fe-spec/generated/order/response/GetExternalOrdersResponse';
import { UserWithOrder } from '@1po/1po-bff-fe-spec/generated/order/response/GetGarageUsersWithOrderResponse';
import { Dispatch } from 'redux';
import { RootState } from 'app/AppStore';
import { isDelegationSessionStatusLoading } from 'domains/delegation/DelegationSession.store';
import { OrderItemLocal } from 'domains/order/Order.types';
import { getUserRights, UserRole } from 'domains/user';
import { InfiniteScrollable } from 'UI';
import { NO_DATA, normalizeVRN, validateVRN } from 'utils';
import {
  fetchAscOrdersRequestSaga,
  fetchClaimsRequestSaga,
  fetchDealerExternalOrdersRequestSaga,
  fetchMyClientsOrderDetailRequestSaga,
  fetchMyClientsOrdersRequestSaga,
  fetchOrderByIdRequestSaga,
  fetchVehicleInfoForClaimByVrnRequestSaga,
  getAscOrdersFilters,
  getClaimsFilters,
  getGarageUsersWithOrder,
  getGarageUsersWithOrderRequestSaga,
  getInProgressOrder,
  getInProgressOrdersHasMore,
  getInProgressOrdersIsLoading,
  getMyClientsOrderDetail,
  getMyClientsOrders,
  orderSubscriptionSaga,
  orderUnsubscribeSaga,
  resetMyClientsOrderDetail,
  subscribeSap2bClaimMessages,
  clearClaimVehicleInfo,
} from './Order.store';

export const useInfiniteScrollInProgressOrders = ({
  orders,
}: {
  orders: OrderItemLocal[];
}): InfiniteScrollable<OrderItemLocal> => {
  const dispatch = useDispatch();
  const hasMore = useSelector(getInProgressOrdersHasMore);
  const isLoading = useSelector(getInProgressOrdersIsLoading);
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);

  function onLoadMore() {
    if (!isLoading && !isDelegationLoading) {
      dispatch(orderSubscriptionSaga({ loadMore: true }));
    }
  }

  return { loadMore: onLoadMore, hasMore, currentData: orders };
};

export const useFetchOrder = (orderId: string) => {
  const dispatch = useDispatch();
  const orderSearchData = useSelector((state: RootState) => getInProgressOrder(state, orderId));
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);

  useEffect(() => {
    if (isDelegationLoading) {
      return;
    }
    if (orderSearchData?.searchStatus !== undefined) {
      return;
    }
    dispatch(fetchOrderByIdRequestSaga(orderId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, orderId, isDelegationLoading]);
};

export const useFetchExternalOrders = (externalOrders: ExternalOrderItem[] | NO_DATA) => {
  const dispatch = useDispatch();
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);

  useEffect(() => {
    if (isDelegationLoading) return;
    if (externalOrders !== undefined) return;

    dispatch(fetchDealerExternalOrdersRequestSaga());
  }, [dispatch, externalOrders, isDelegationLoading]);
};

export const useSubscribeSap2bClaimMessages = () => {
  const userRights = useSelector(getUserRights);
  const dispatch = useDispatch();
  useEffect(() => {
    if (userRights.includes(UserRole.R1) || userRights.includes(UserRole.R1_ORDER_AND_CLAIM)) {
      dispatch(subscribeSap2bClaimMessages());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRights]);
};

export const useFetchStoredClaims = () => {
  const dispatch = useDispatch();
  const claimsFilters = useSelector(getClaimsFilters);
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);

  useEffect(() => {
    if (isDelegationLoading) {
      return;
    }
    dispatch(fetchClaimsRequestSaga({ cursor: undefined }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimsFilters, isDelegationLoading]);
};

export const useFetchAscOrders = () => {
  const dispatch = useDispatch();
  const ascOrdersFilters = useSelector(getAscOrdersFilters);
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);

  useEffect(() => {
    if (isDelegationLoading) {
      return;
    }
    dispatch(fetchAscOrdersRequestSaga({ cursor: undefined }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ascOrdersFilters, isDelegationLoading]);
};

export const useFetchMyClientsOrders = () => {
  const dispatch = useDispatch();
  const orders = useSelector(getMyClientsOrders);
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);

  useEffect(() => {
    if (!isDelegationLoading) {
      dispatch(fetchMyClientsOrdersRequestSaga({}));
    }
  }, [dispatch, isDelegationLoading]);

  return orders;
};

export const useFetchGarageUsersWithOrder = (): UserWithOrder[] => {
  const dispatch = useDispatch();
  const garageUsersWithOrder = useSelector(getGarageUsersWithOrder);
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);

  useEffect(() => {
    if (isDelegationLoading) {
      return;
    }
    dispatch(getGarageUsersWithOrderRequestSaga());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDelegationLoading]);

  return garageUsersWithOrder;
};

export const useSubscribeOrderUpdates = () => {
  const dispatch = useDispatch();
  const userRights = useSelector(getUserRights);

  useEffect(() => {
    const hasOrderRights =
      userRights.includes(UserRole.R1) ||
      userRights.includes(UserRole.COMMAND) ||
      userRights.includes(UserRole.CONNECT_COMMANDE);

    if (!hasOrderRights) {
      return undefined;
    }

    dispatch(orderSubscriptionSaga());

    return () => {
      dispatch(orderUnsubscribeSaga());
    };
  }, [dispatch, userRights]);
};

export const useFetchMyClientsOrderDetail = (orderNumber: string) => {
  const dispatch = useDispatch();
  const selectedOrder = useSelector(getMyClientsOrderDetail);

  useEffect(() => {
    dispatch(fetchMyClientsOrderDetailRequestSaga({ orderNumber }));
    return () => {
      dispatch(resetMyClientsOrderDetail());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  return selectedOrder;
};

export const fetchVehicleInfoByVrn = (dispatch: Dispatch, vrn: string) => {
  if (!vrn || vrn.trim().length === 0) {
    dispatch(clearClaimVehicleInfo());
    return;
  }
  const normalizedVRN = normalizeVRN(vrn.trim());
  if (validateVRN(normalizedVRN)) {
    dispatch(fetchVehicleInfoForClaimByVrnRequestSaga({ vrn: normalizedVRN }));
  } else {
    dispatch(clearClaimVehicleInfo());
  }
};
